import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Calendar, Clock, DollarSign, ClipboardList, Printer, ArrowLeft, User, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * BookingDetail — full page for a single booking.
 *
 * Reached by tapping any booking row on /wallet. Practitioners come here
 * to review a client's intake answers before every session (with a Print
 * button that gets the whole card ready to hand-off to the client), and
 * clients come here to review what they submitted + when the session is.
 */
export default function BookingDetail() {
  const { bid } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // The /bookings endpoint already returns the caller's booking
        // rows joined with service; find the one we care about client-side
        // rather than adding a new detail endpoint. Practitioners see
        // theirs, clients see theirs — auth is enforced by the list route.
        const { data } = await api.get("/bookings");
        const b = (data || []).find((x) => x.id === bid);
        if (!b) throw new Error("not found");
        setBooking(b);
      } catch {
        toast.error("Booking not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [bid]);

  if (loading) {
    return (
      <Layout>
        <div className="text-foreground/60 flex items-center gap-2 p-10">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading booking…
        </div>
      </Layout>
    );
  }

  if (!booking) {
    return (
      <Layout>
        <div className="p-10 text-foreground/60">
          <Link to="/wallet" className="text-secondary hover:underline text-xs uppercase tracking-wider">← Back to wallet</Link>
          <p className="mt-4">This booking doesn&apos;t exist or you don&apos;t have access to it.</p>
        </div>
      </Layout>
    );
  }

  const svc = booking.service || {};
  const client = booking.client || {};
  const provider = booking.provider || {};
  const scheduled = booking.scheduledAt || booking.slotAt;
  const scheduledPretty = scheduled
    ? new Date(scheduled).toLocaleString(undefined, {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "Time TBD";
  const intake = booking.intakeAnswers || {};
  const intakeQuestions = svc.intakeQuestions || [];
  const qById = Object.fromEntries(intakeQuestions.map((q) => [q.id, q]));
  const intakeRows = Object.entries(intake)
    .map(([qid, val]) => ({
      label: (qById[qid] && qById[qid].label) || qid,
      display: Array.isArray(val) ? val.join(", ") : String(val ?? ""),
    }))
    .filter((r) => r.display);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="booking-detail-page">
        <div className="flex items-center justify-between print:hidden">
          <Link to="/wallet" className="text-xs uppercase tracking-wider text-secondary hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to bookings
          </Link>
          <button
            onClick={() => window.print()}
            className="border border-secondary/40 hover:bg-secondary/10 px-4 py-2 rounded-sm inline-flex items-center gap-2 text-xs uppercase tracking-wider"
            data-testid="booking-print-btn"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>

        <div className="parchment p-6 space-y-2">
          <div className="label-mystic text-secondary">Booking</div>
          <h1 className="font-serif text-4xl">{svc.title || "Session"}</h1>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-foreground/70">
            <div className="inline-flex items-center gap-2">
              <Calendar className="w-4 h-4 text-secondary" /> {scheduledPretty}
            </div>
            {svc.durationMin && (
              <div className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-secondary" /> {svc.durationMin} min
              </div>
            )}
            {booking.amount != null && (
              <div className="inline-flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-secondary" /> ${Number(booking.amount).toFixed(2)}
              </div>
            )}
            <div className="inline-flex items-center gap-2">
              <span className="uppercase text-[10px] tracking-wider">Status</span>
              <span className="text-secondary font-serif">{booking.status || "scheduled"}</span>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="parchment p-5" data-testid="booking-provider">
            <div className="label-mystic text-foreground/60 mb-2">Practitioner</div>
            <div className="flex items-center gap-3">
              {provider.avatarUrl ? (
                <img src={provider.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-foreground/40 p-1.5 border border-secondary/25 rounded-full" />
              )}
              <div>
                <div className="font-serif text-lg">{provider.name || provider.businessName || "—"}</div>
                {provider.email && <div className="text-[11px] text-foreground/55">{provider.email}</div>}
              </div>
            </div>
          </div>
          <div className="parchment p-5" data-testid="booking-client">
            <div className="label-mystic text-foreground/60 mb-2">Client</div>
            <div className="flex items-center gap-3">
              {client.avatarUrl ? (
                <img src={client.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-foreground/40 p-1.5 border border-secondary/25 rounded-full" />
              )}
              <div>
                <div className="font-serif text-lg">{client.name || "—"}</div>
                {client.email && <div className="text-[11px] text-foreground/55">{client.email}</div>}
              </div>
            </div>
          </div>
        </div>

        {intakeRows.length > 0 && (
          <div className="parchment p-6" data-testid="booking-intake">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-4 h-4 text-secondary" />
              <div className="label-mystic text-secondary">Intake answers</div>
            </div>
            <dl className="space-y-3">
              {intakeRows.map((r, i) => (
                <div key={i} className="grid sm:grid-cols-[minmax(180px,220px)_1fr] gap-3 items-start border-b border-foreground/5 pb-3 last:border-0">
                  <dt className="text-[11px] uppercase tracking-wider text-foreground/60">{r.label}</dt>
                  <dd className="text-sm text-foreground/90 whitespace-pre-wrap break-words">{r.display}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {svc.description && (
          <div className="parchment p-6">
            <div className="label-mystic text-secondary mb-2">Session description</div>
            <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">{svc.description}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
